import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { v4 as uuid } from "uuid";
import {
  clearGlobalStorage,
  getAllSessionKeys,
  getAllStorageKeys,
  readFromGlobalStorage,
  writeAtomicGlobalStorage,
  writeToGlobalStorage,
} from "fe-web-components/userStorage/globalUserStorage";
import { UserStorageKey } from "fe-web-components/userStorage/userStorageKey";
import { getFeatureFlags } from "fe-common/featureFlags/getFeatureFlags";

export const SESSION_TIME_OUT_MINUTES = 3 * 60; // Session time is set to 3 hours of inactivity

export const SESSION_TIME_OUT_MILLI = SESSION_TIME_OUT_MINUTES * 60 * 1000;

/***
 * Maximal number of active sessions supported per client
 */
const MAX_NUMBER_OF_SESSION_TO_STORE = 5;

export enum SessionInvalidationReasons {
  Timeout = "timeout",
  OtherSessionTookOver = "otherSessionTookOver",
}

export interface SessionState {
  currentSession: Session;
  isSessionValid: boolean;
  showSessionInfo: boolean;
  // If this session was loaded form an earlier session
  basedOn: string;
  invalidationReason?: SessionInvalidationReasons;
}

export type Session = {
  sessionId: string;
  lastActivity: Date;
  // Id of previous session
  basedOn?: string;
};

export type UseSessionStateApi = {
  session: SessionState;
  updateSession: () => void;
  resetSession: () => void;
};

export const translateToSessionKey = (key: UserStorageKey, sessionId: string) =>
  `${sessionId}-${key}`;

export const getLastSessions: () => Session[] = () => {
  const byDate = (a: Session, b: Session) => {
    // console.log("sessionDebug - comparing two sessions", a, b);
    let result = (new Date(a?.lastActivity) -
      new Date(b?.lastActivity)) as number;
    // console.log("sessionDebug - compare result", result);
    return result;
  };
  return getAllStorageKeys(UserStorageKey.Session)
    .map((sessionKey) => readFromGlobalStorage<Session>(sessionKey) as Session)
    .sort(byDate);
};

/***
 * Reads all session keys from local storage.
 * session keys are sored in the format X-UserStorageKey.Session, when X is the
 * session id. So we order them by the last activity and pop the last one.
 */
export const getLastSessionFromLocalStore: () => Session = () => {
  const sortedKeys = getLastSessions();
  // console.log(
  //   "sessionDebug -getting last session from local store",
  //   sortedKeys,
  // );
  const lastKey = sortedKeys.pop();

  // keep only top 5 sessions/tabs
  sortedKeys.slice(MAX_NUMBER_OF_SESSION_TO_STORE).forEach((session) =>
    // remove invalid sessions
    getAllSessionKeys(session.sessionId)?.forEach((key) =>
      clearGlobalStorage(key),
    ),
  );

  // console.log("sessionDebug -Returning", lastKey);
  return lastKey;
};

const isValidSession = (session: Session) =>
  session &&
  session.lastActivity &&
  new Date().getTime() - new Date(session.lastActivity).getTime() <
    SESSION_TIME_OUT_MILLI;

export const useSessionState = () => {
  const initialValue = useMemo(() => {
    const lastSession = getLastSessionFromLocalStore();
    const sessionId = uuid();
    const preSessionId = isValidSession(lastSession)
      ? lastSession.sessionId
      : null;
    const currentSession = {
      sessionId,
      lastActivity: new Date(),
      basedOn: preSessionId,
    } as Session;
    const currentKey = translateToSessionKey(UserStorageKey.Session, sessionId);
    // Mark this session as the next one to become valid
    writeToGlobalStorage(currentKey, JSON.stringify(currentSession));
    return {
      currentSession,
      basedOn: preSessionId,
      isSessionValid: true,
      showSessionInfo: true,
    };
  }, []);

  const showSessionInfo = getFeatureFlags("showSessionInfo");

  const [sessionValue, setSession] = useState({
    ...initialValue,
    showSessionInfo,
  });

  // Use a state to avoid over-updating the storage
  const [shouldUpdate, setShouldUpdate] = useState(false);

  const writeSession = useCallback(() => {
    setShouldUpdate(true);
  }, [setShouldUpdate]);

  useEffect(() => {
    if (!shouldUpdate) return;
    setShouldUpdate(false);
    writeAtomicGlobalStorage(
      translateToSessionKey(
        UserStorageKey.Session,
        sessionValue.currentSession.sessionId,
      ),
      (lastSession: Session) => {
        // console.log(
        //   `session debug - updating session storage [isValid:${isValidSession(
        //     lastSession,
        //   )}, delta:${new Date(lastSession.lastActivity).getTime() -
        //     new Date().getTime()}]`,
        //   lastSession,
        //   sessionValue.currentSession,
        // );
        // if no earlier session were set, just return the current
        if (!lastSession) return sessionValue.currentSession;

        // If local storage has the same id, and it wasn't yet timed out
        // mark it as updated and valid
        if (
          lastSession &&
          isValidSession(sessionValue.currentSession) &&
          lastSession.sessionId === sessionValue.currentSession.sessionId
        ) {
          const updatedSession = {
            isSessionValid: true,
            basedOn: lastSession.sessionId,
            showSessionInfo,
            currentSession: {
              ...lastSession,
              lastActivity: new Date(),
            } as Session,
          } as SessionState;
          setSession(updatedSession);
          return updatedSession.currentSession;
        }
        // Session is invalid, mark as invalid
        const invalidationReason = !isValidSession(sessionValue.currentSession)
          ? SessionInvalidationReasons.Timeout
          : SessionInvalidationReasons.OtherSessionTookOver;
        // Otherwise spoil the session
        const updatedSession: SessionState = {
          ...sessionValue,
          isSessionValid: false,
          invalidationReason,
          currentSession: {
            ...sessionValue.currentSession,
          } as Session,
        } as SessionState;
        console.log(
          "Session invalidated last id:" +
            lastSession.sessionId +
            "current:" +
            sessionValue.currentSession.sessionId,
        );
        setSession(updatedSession);
        return lastSession;
      },
    );
  }, [
    sessionValue.currentSession,
    showSessionInfo,
    setShouldUpdate,
    shouldUpdate,
  ]);

  const updateSession = useCallback(() => {
    writeSession();
  }, [writeSession]);

  const resetSession = useCallback(() => {
    setSession((oldSession) => {
      const newSession = {
        ...oldSession,
        currentSession: {
          ...oldSession.currentSession,
          lastActivity: new Date(),
        },
        isSessionValid: true,
      };
      console.log("resetting session", newSession);
      return newSession;
    });
  }, [setSession, writeSession]);

  return {
    session: sessionValue,
    updateSession,
    resetSession,
  } as UseSessionStateApi;
};

export const sessionContextObject = createContext(null);

export const SessionContext = React.memo(({ children = null }) => {
  const ContextProvider = sessionContextObject.Provider;
  const state = useSessionState();
  // console.log("Session context created", state?.[0]);
  return <ContextProvider value={state}>{children}</ContextProvider>;
});

const emptySession = {};

export const useSession = () =>
  useContext(sessionContextObject) || emptySession;
